/**
 * Was vorgeladen wird -- und was einmal weg ist (7.69n, 7.83).
 *
 * Die Ausführung steht in der App (`apps/web/src/vorladen.ts`): sie holt die
 * Dateien in den Zwischenspeicher und weiter in den Arbeitsspeicher. Hier
 * steht nur die Entscheidung, welche Adressen überhaupt drankommen -- damit
 * sie geprüft ist und nicht mitten in einer Schleife verborgen liegt, so wie
 * die Regel für den Arbeitsspeicher (`ramPlan`).
 *
 * Dazugekommen ist der Fall, den der Durchgang am 13. September gefunden hat:
 * ein Medium wird gelöscht, während ein anderes Fenster seine Liste noch
 * hält. Dessen Vorladen läuft weiter und holt eine Datei, die es nicht mehr
 * gibt -- 404. Dieses eine Mal ist richtig so: anders erfährt das Fenster es
 * nicht. Falsch wäre das zweite Mal. Also merkt sich das Vorladen die
 * Adresse und überspringt sie von da an; `fuerImmerWeg` sagt, wann eine
 * Antwort das bedeutet.
 */
/** Mehr als das wird je Datei nicht vorgeladen. */
export const VORLADEN_JE_DATEI = 120 * 1024 * 1024;
/** Und mehr als das insgesamt nicht; darüber fliegt das Älteste heraus. */
export const VORLADEN_GESAMT = 400 * 1024 * 1024;
/**
 * Der Schlüssel, unter dem eine Medien-Adresse wiedererkannt wird.
 *
 * `/api/media?t=…` trägt eine unterschriebene Marke, und die wird bei jedem
 * Ausstellen neu gemacht: sie enthält einen Ablauf. Zwei Adressen auf
 * dieselbe Datei sehen deshalb verschieden aus -- und ein Gedächtnis, das
 * sich die Adresse merkt, erkennt die Datei beim nächsten Mal nicht wieder.
 * Gemerkt wird darum der Pfad aus der offenen Mitte der Marke; ist da keiner
 * zu lesen, bleibt die Adresse selbst der Schlüssel.
 *
 * Gelesen, nicht geprüft: ob die Marke gilt, entscheidet der Server.
 */
export function medienSchluessel(adresse) {
    const treffer = /[?&]t=([^&]+)/.exec(adresse);
    if (!treffer)
        return adresse;
    try {
        const mitte = decodeURIComponent(treffer[1]).split('.')[1];
        if (!mitte)
            return adresse;
        const roh = mitte.replace(/-/g, '+').replace(/_/g, '/');
        // Der Pfad besteht aus Projekt- und Medien-Kennungen: reines ASCII.
        const text = typeof atob === 'function' ? atob(roh) : Buffer.from(roh, 'base64').toString('utf8');
        const pfad = JSON.parse(text).p;
        return typeof pfad === 'string' && pfad ? pfad : adresse;
    }
    catch {
        return adresse;
    }
}
/**
 * 404 und 410 heissen: die Datei ist weg, nicht bloss gerade nicht da.
 *
 * Alles andere -- 500, 502, ein abgerissenes Netz -- ist ein Zustand, der
 * vorbeigeht; solche Adressen bleiben in der Liste und werden beim nächsten
 * Mal wieder versucht.
 */
export function fuerImmerWeg(status) {
    return status === 404 || status === 410;
}
/**
 * Welche Dateien vorzuladen sind, in der Reihenfolge ihres ersten Auftritts
 * im Schnitt. Was als verschollen bekannt ist, kommt nicht mehr vor.
 */
export function vorladeListe(medien, reihenfolge, 
/** Schlüssel im Sinne von `medienSchluessel`, nicht ganze Adressen. */
verschollen = new Set(), jeDatei = VORLADEN_JE_DATEI) {
    const rang = new Map(reihenfolge.map((id, i) => [id, i]));
    return medien
        .filter((a) => a.status === 'ready' &&
        !!a.proxyUrl &&
        !verschollen.has(medienSchluessel(a.proxyUrl)) &&
        (a.kind === 'video' || a.kind === 'audio') &&
        // Vier Mal die Grenze: was beim Holen abgeschnitten wird, entscheidet
        // die angekündigte Länge -- die Angabe aus der Datenbank ist nur eine
        // grobe Vorauswahl, damit ein Rohmaterial von Gigabytes gar nicht
        // erst in die Reihe kommt.
        a.sizeBytes <= jeDatei * 4)
        .sort((a, b) => (rang.get(a.id) ?? 1e9) - (rang.get(b.id) ?? 1e9));
}
//# sourceMappingURL=vorladen.js.map